import React, { useCallback, useEffect, useMemo } from "react";
import compact from "lodash/compact";
import get from "lodash/get";
import { v4 as uuid } from "uuid";

import {
  Alert as BaseAlert,
  AlertProps,
  AlertTitle,
  Color as AlertColor,
} from "@material-ui/lab";
import {
  Box,
  Collapse,
  fade,
  Grid,
  IconButton,
  List,
  ListItem,
  styled,
  Typography,
} from "@material-ui/core";
import { ExtendedContainer } from "../ExtendedContainer";
import CloseIcon from "@material-ui/icons/Close";
import {
  ErrorIcon as BaseErrorIcon,
  InfoIcon as BaseInfoIcon,
  SuccessIcon as BaseSuccessIcon,
  WarningIcon as BaseWarningIcon,
} from "../../icons";
import { TextList } from "../TextList";

const ErrorIcon = styled(BaseErrorIcon)(({ theme }) => ({
  fill: theme.palette.common.white,
}));
const InfoIcon = styled(BaseInfoIcon)(({ theme }) => ({
  fill: theme.palette.common.white,
}));
const SuccessIcon = styled(BaseSuccessIcon)(({ theme }) => ({
  fill: theme.palette.common.white,
}));
const WarningIcon = styled(BaseWarningIcon)(({ theme }) => ({
  fill: theme.palette.common.white,
}));

export interface ExtendedAlertProps extends AlertProps {
  bgColor: string;
}
const Alert = styled(({ bgColor, ...other }) => <BaseAlert {...other} />)(
  ({ bgColor, theme }) => ({
    padding: "0px",
    backgroundColor: fade(get(theme, `palette.${bgColor}`), 0.04),
  }),
) as React.FC<ExtendedAlertProps>;

const WhitespaceAwareTypography = styled(Typography)(() => ({
  whiteSpace: "pre-line",
}));

// Alert Variants
type Variant = "warning" | "error" | "info" | "success";
const getVariantDetails = (variant: Variant) => {
  if (variant === "error") {
    return {
      icon: <ErrorIcon data-testid="AlertBlock__Icon--error" />,
      color: "error.main",
    };
  } else if (variant === "warning") {
    return {
      icon: <WarningIcon data-testid="AlertBlock__Icon--warning" />,
      color: "warning.main",
    };
  } else if (variant === "success") {
    return {
      icon: <SuccessIcon data-testid="AlertBlock__Icon--success" />,
      color: "success.main",
    };
  }
  return {
    icon: <InfoIcon data-testid="AlertBlock__Icon--info" />,
    color: "info.main",
  };
};

//Repeating Set of Alert Messages
export const AlertList: React.FC = ({ children }) => {
  const count = React.Children.count(children);

  if (count > 1) {
    return (
      <List dense>
        {React.Children.map(children, (child, i) => {
          // For elements use their key, For strings they are the key
          const key = React.isValidElement(child)
            ? child.key || undefined
            : undefined;
          return (
            <ListItem disableGutters key={key}>
              {child}
            </ListItem>
          );
        })}
      </List>
    );
  }

  return <>{children}</>;
};

export interface AlertBlockProps extends ExtendedContainer {
  title?: string;
  messages?: Array<string | React.ReactNode>;
  variant: AlertColor;
  list?: boolean;
  isClosable?: boolean;
  onClose?: Function;
  onMount?: Function;
  alertProps?: AlertProps | { [key: string]: any };
  footer?: React.ReactNode;
}

/**
 * A flexible alert block used to display additional information, warnings, errors and success alerts to the user
 * @param title - An optional title for the alert
 * @param footer - Additional content at the bottom of the block
 * @param baseAlerts - List of messages displayed in the alert, block is not displayed if empty
 * @param variant - Color theme of the block success/info/warning/error
 * @param list - Alerts displayed as bullet list
 * @param isClosable - Allow the alert to be collapsed/closed
 * @param onClose - Handler function on close of the alert
 * @param onMount - Handle function on mount of the alert
 * @param alertProps - Passthrough of the native MUI Alert props
 */
export const AlertBlock: React.FC<AlertBlockProps> = ({
  title,
  footer,
  messages: baseAlerts,
  variant,
  list = false,
  isClosable = true,
  onClose,
  onMount,
  alertProps,
  ...props
}) => {
  useEffect(() => {
    if (onMount) {
      onMount();
    }
  }, [onMount]);

  const alerts = useMemo(() => {
    return compact(baseAlerts).map((alert) => ({
      key: `AlertBlock__Item--${uuid()}`,
      message: typeof alert === "function" ? alert() : alert,
    }));
  }, [baseAlerts]);

  const [open, setOpen] = React.useState(true);
  const handleClose = useCallback(() => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const { icon, color } = useMemo(() => {
    return getVariantDetails(variant);
  }, [variant]);

  // If there are no errors, bail…
  if (!alerts.length) {
    return null;
  }

  return (
    <ExtendedContainer
      maxWidth="none"
      mx={0}
      data-testid="AlertBlock"
      disableGutters
      {...props}
    >
      <Collapse in={open}>
        <Box my={1.5}>
          <Alert
            severity={variant}
            variant="outlined"
            bgColor={color}
            icon={
              <Box height="100%" bgcolor={color} width={1} px={2.5} clone>
                <Grid container alignItems="center">
                  <Grid item>{icon}</Grid>
                </Grid>
              </Box>
            }
            action={
              isClosable ? (
                <Box color={color}>
                  <IconButton
                    aria-label="close error"
                    color="inherit"
                    size="small"
                    onClick={handleClose}
                    data-testid="AlertBlock__Button"
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ) : null
            }
            {...alertProps}
          >
            <Box py={0.5} pr={2}>
              {title && (
                <AlertTitle data-testid="AlertBlock__Title">{title}</AlertTitle>
              )}
              {list ? (
                <TextList>
                  {alerts?.map((a) => (
                    <li key={a.key}>{a.message}</li>
                  ))}
                </TextList>
              ) : (
                <AlertList>
                  {alerts?.map((alert) => (
                    <WhitespaceAwareTypography
                      key={alert.key}
                      data-testid="AlertBlock__Item"
                    >
                      {alert.message}
                    </WhitespaceAwareTypography>
                  ))}
                </AlertList>
              )}
            </Box>
            {footer ? footer : null}
          </Alert>
        </Box>
      </Collapse>
    </ExtendedContainer>
  );
};
